import { useEffect, useState } from 'react';
import usePrevValue from '../../hooks/usePrevValue';
import { primitiveListsAreEqual } from '../../util/listutils';

type SearchElementToStringFunc<T extends unknown> = (element: T) => string;

type SearchWordElementToStringPair<T extends unknown> = [
  string,
  SearchElementToStringFunc<T>,
][];

const DEAFULT_SEARCH_LENGTH = 2;

const searchInString = <T extends unknown>(
  searchWord: string,
  elementToString: SearchElementToStringFunc<T>,
  element: T,
  minSearchLength: number,
): boolean =>
  searchWord.length < minSearchLength ||
  elementToString(element)
    .toLowerCase()
    .indexOf(searchWord.toLocaleLowerCase()) > -1;

const searchMultiple = <T extends unknown>(
  elements: T[],
  searchWordElementToStringPairs: SearchWordElementToStringPair<T>,
  minSearchLength: number = DEAFULT_SEARCH_LENGTH,
): T[] =>
  elements.filter((element) =>
    searchWordElementToStringPairs.every(([searchWord, elementToString]) =>
      searchInString(searchWord, elementToString, element, minSearchLength),
    ),
  );

const mapToSearchWords = <T extends unknown>(
  searchWordElementToStringPairs: SearchWordElementToStringPair<T>,
): string[] => searchWordElementToStringPairs.map(([searchWord]) => searchWord);

const noSearchWordHasBeenPruned = (
  updatedSearchWords: string[],
  prevSearchWords: string[],
): boolean =>
  prevSearchWords.every(
    (prevSearchWord, index) =>
      updatedSearchWords[index].indexOf(prevSearchWord) > -1,
  );

const useTableSearch = <T extends unknown>(
  elements: T[],
  searchWordElementToStringPairs: SearchWordElementToStringPair<T>,
  minSearchLength?: number,
): T[] => {
  const [result, setResult] = useState(elements);

  const prevSearches = usePrevValue(
    mapToSearchWords(searchWordElementToStringPairs),
  );
  const prevResult = usePrevValue(result);

  useEffect(() => {
    const updatedSearchWords = mapToSearchWords(searchWordElementToStringPairs);

    const prevResultOrElements =
      prevSearches &&
      prevResult &&
      noSearchWordHasBeenPruned(updatedSearchWords, prevSearches)
        ? prevResult
        : elements;

    if (
      !prevSearches ||
      !primitiveListsAreEqual(updatedSearchWords, prevSearches)
    ) {
      setResult(
        searchMultiple(
          prevResultOrElements,
          searchWordElementToStringPairs,
          minSearchLength,
        ),
      );
    }
  }, [
    prevSearches,
    prevResult,
    elements,
    searchWordElementToStringPairs,
    minSearchLength,
  ]);

  return result;
};

export default useTableSearch;
